/**
 * Global Error Page (Next.js App Router)
 * Catches errors that occur outside React rendering (rare but critical)
 */

'use client';

import React from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';
import { cn } from '@/lib/utils';

interface GlobalErrorProps {
  error: Error & { digest?: string };
  reset: () => void;
}

export default function GlobalError({ error, reset }: GlobalErrorProps) {
  // Log error
  if (typeof window !== 'undefined') {
    console.error('Global error (outside React):', error);
    
    // Optional: Send to error tracking service
    if ((window as any).Sentry) {
      (window as any).Sentry.captureException(error);
    }
  }

  return (
    <html lang="en">
      <body className="bg-slate-950">
        <div className="min-h-screen flex items-center justify-center p-6">
          <div className="max-w-2xl w-full">
            <div className="bg-slate-900/95 backdrop-blur-xl border border-white/10 rounded-2xl p-8 shadow-2xl">
              {/* Icon */}
              <div className="flex items-center justify-center mb-6">
                <div className="rounded-full bg-rose-500/20 p-4">
                  <AlertTriangle className="h-12 w-12 text-rose-400" />
                </div>
              </div>

              {/* Title */}
              <h1 className="text-2xl font-bold text-white text-center mb-3">
                Application Error
              </h1>
              <p className="text-slate-400 text-center mb-8">
                A critical error occurred. Please refresh the page or contact support if the problem persists.
              </p>

              {/* Actions */}
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => window.location.reload()}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 px-4 py-3',
                    'rounded-xl border border-emerald-500/30 bg-emerald-500/10',
                    'text-emerald-200 font-semibold',
                    'hover:bg-emerald-500/20 hover:border-emerald-500/50',
                    'transition-colors'
                  )}
                >
                  <RefreshCw className="h-4 w-4" />
                  Reload Page
                </button>
                <button
                  onClick={() => {
                    window.location.href = '/dashboard';
                  }}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 px-4 py-3',
                    'rounded-xl border border-white/10 bg-white/5',
                    'text-white font-semibold',
                    'hover:bg-white/10 hover:border-white/20',
                    'transition-colors'
                  )}
                >
                  <Home className="h-4 w-4" />
                  Go to Dashboard
                </button>
              </div>

              {/* Error Details */}
              {process.env.NODE_ENV === 'development' && (
                <div className="mt-6 p-4 rounded-lg bg-slate-950/50 border border-white/5">
                  <div className="text-xs font-mono text-rose-400 break-all">
                    {error.message}
                  </div>
                  {error.stack && (
                    <pre className="text-xs font-mono text-slate-400 mt-2 whitespace-pre-wrap break-all">
                      {error.stack}
                    </pre>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </body>
    </html>
  );
}
